import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { ModalBigCentered, ButtonsModalSmallCentered } from '../../../../../../../baseComponents/Modal'
import { OrderWorkingCenterIdCustomSelect, OrderCurrencyIdCustomSelect } from '../../../../../../../baseComponents/ModalsLayouts/ModalOrderIncompatibles/Subcomponents'
import { permissionCheck } from '../../../../../../../baseComponents/PermissionChecker/function'
import twinFetchPost from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getPreparePricesConfigParam, getUserPermissions } from '../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { parseMultiSelectedTableForProcessing } from '../../../../../../inputTableLayouts/order/OrderLayout/useMultiOrderScreenLogic/functions'
import { ModalProcessMultiSellBaseLogicProps, ModalProcessMultiSellBaseProps } from '../../types'
import { SetEditDeleteModalTy } from '../../../../../../../utils/hooks/useEditDeleteModal'
import { CalculateParamsTy } from '../../../../../../../baseComponents/Displays/DisplayPrice/types'
import { HeaderDataCalculate } from '@teinor/erp/types/company/order/general'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'
import { parseOrderTableData } from '../../../../../../inputTableLayouts/order/OrderQuoteLayout/function'
import '../modalProcessMultiSell.sass'


type ModalProcessMultiSellWithStockProps = ModalProcessMultiSellBaseProps  & ModalProcessMultiSellWithStockLogicProps & { }

const ModalProcessMultiSellWithStock: React.FC<ModalProcessMultiSellWithStockProps> = ({ setOpenModal, data, conflicts, sellInfo, translateText, urlProcess, urlNavigate,  ...logic}) => {
    const { handleConflict, mySellInfo, finalizeOrder, loading } = useModalProcessMultiSellWithStockLogic({ data, sellInfo, setOpenModal, urlProcess, urlNavigate, ...logic })
    const {t} = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    const dictConflictComponents: TwinDictionary = {
        'WorkingCenterId': { component: OrderWorkingCenterIdCustomSelect, extraData: {label: t('soldBy', 'Vendido por')}},
        'CurrencyId':  { component: OrderCurrencyIdCustomSelect} 
    }
    for (const key in conflicts.conflicts) {
        const conflict = conflicts.conflicts[key]
        if(dictConflictComponents?.[conflict]){
            const Component = dictConflictComponents?.[conflict].component
            renderThis.push(<Component field={conflict} {...{ handleConflict, ...dictConflictComponents?.[conflict]?.extraData}} header={mySellInfo} pos={parseInt(key)} />)
        }
    }
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpenModal(null)} onClickOut={false} className='flex modal_process_multi_sell'>
            <div className='flex flex-col modal_process_multi_sell_container'>
                <div className='process_buy_order'>
                    <h4 className=''>{translateText.titleText}</h4>
                </div>
                {conflicts.conflicts?.length ? 
                    <div className='my-20'>
                        <div className='text-left'>
                            {translateText.conflictText}
                        </div>
                        <div className='my-30'>
                            {renderThis}
                        </div>
                    </div> 
                    : <div className='my-20'>
                        {translateText.confirmText}
                    </div> 
                }
                <ButtonsModalSmallCentered confirmText={t('create', 'Crear')} onCancel={() => setOpenModal(null)} setOpened={() => {}} onAccept={() => finalizeOrder(urlProcess, urlNavigate)} loading={loading} />
            </div>
        </ModalBigCentered>
    )
}
interface ModalProcessMultiSellWithStockLogicProps extends ModalProcessMultiSellBaseLogicProps {
    setOpenModal: SetEditDeleteModalTy
}

const useModalProcessMultiSellWithStockLogic = ({ data, sellInfo, setOpenModal, urlNavigate, urlProcess }: ModalProcessMultiSellWithStockLogicProps) => {
    const {t} = useTwinTranslation()
    const {loading, startLoading} = useIsLoading()
    const [mySellInfo, setMySellInfo] = useState<TwinDictionary>(sellInfo)
    const navigate = useNavigate()

    const handleConflict = useCallback((field: string, value: string) => {
        setMySellInfo((old) => {
            if (!old) {
                return null
            }
            const parsedOld = JSON.parse(JSON.stringify(old))
            parsedOld[field] = value
            return { ...parsedOld }
        })
    }, [])
    const noProductsMsg = t('notProcessedAnyProducts', 'No has procesado ningún producto')
    const errorMsg = t('noProductStockOnLine', 'No hay stock del producto en la línea')
    const errorQuantityRequired = t('quantityIsRequired', 'Es necesario procesar productos con cantidad')
    const finalizeOrder = useCallback(async (url: string, navigateUrl?: string, extraProps?: TwinDictionary) => {
        startLoading()
        const selectedProducts = parseMultiSelectedTableForProcessing(data)
        delete sellInfo.Customer
        if (selectedProducts.forSend.products.length) {
            const response = await twinFetchPost(url, { selectedProducts: selectedProducts.forSend.products, sellInfo: { ...sellInfo, ...mySellInfo }, ...extraProps })
            const responseJSON = await response.json()
            if (response.status === 200) {
                navigate(urlNavigate + '/' + responseJSON.id)
                setOpenModal(null)
            } else {
                if (responseJSON.errors[0].msg) {
                    if (responseJSON.errors[0].msg === 'noProducts') {
                        changeErrorMessage(noProductsMsg)
                        setOpenModal(null)
                    } else if (responseJSON.errors[0].msg === 'Products are required') {
                        changeErrorMessage(errorQuantityRequired)
                        setOpenModal(null)
                    } else if (responseJSON.errors[0].msg === 'Not payment method') {
                        const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
                        const headerDataCalculate: HeaderDataCalculate = {
                            OrderCosts: data?.header?.OrderCosts,
                            ParamsRoundingDecimals: calculateParams,
                            OrderShipping: data?.header?.OrderShipping,
                            isFinished: data?.header?.isFinished,
                            OrderAccounts: data?.header?.OrderAccounts
                        }
                        let selectedTableForCalculate = data?.tableData
                        if (data?.selectedTableData) {
                            selectedTableForCalculate = []
                            for (const element of data?.selectedTableData) {
                                selectedTableForCalculate.push(data?.tableData[data?.dictPosTableData[element.id]])
                            }
                        }
                        const totalsOrderTableData = parseOrderTableData(selectedTableForCalculate as OrderTableData, headerDataCalculate)
                        setOpenModal({ type: 'delete', allRowData: { extraProps: { OrderId: data?.header?.id, WorkingCenterId: data?.header?.WorkingCenterId, selectedProducts: selectedProducts.forSend.products }, createUrl: url, navigateUrl, total: totalsOrderTableData.totals.total, finalizeOrder } })
                    } else if (responseJSON.errors[0].msg.includes('noStock')) {
                        const skipStock = permissionCheck(getUserPermissions('sellOther.sellWithoutStock'), 'read')
                        if (skipStock) {
                            setOpenModal({ type: 'edit', allRowData: { extraProps: { OrderId: data?.header?.id, selectedProducts: selectedProducts.forSend.products, WorkingCenterId: data?.header?.WorkingCenterId, skipCheckOperateStock: true }, finalizeOrder, createUrl: url, navigateUrl, data } })
                        } else {
                            const msgArr = responseJSON.errors[0].msg.split('-')
                            let myMsg = errorMsg + ' ' + msgArr[1]
                            if (msgArr[2]) {
                                myMsg += ' nº: ' + msgArr[2]
                            }
                            changeErrorMessage(myMsg)
                            setOpenModal(null)
                        }
                    } else {
                        changeErrorMessage(responseJSON.errors[0].msg)
                        setOpenModal(null)
                    }
                }
            }
        }else {
            changeErrorMessage(errorQuantityRequired)
            setOpenModal(null)
        }
    }, [navigate, setOpenModal, data, sellInfo, mySellInfo, noProductsMsg, errorQuantityRequired, errorMsg, startLoading, urlNavigate])
    
    return { handleConflict, mySellInfo, finalizeOrder, loading }
}

export default ModalProcessMultiSellWithStock